import styled from 'styled-components';

import { AddCircleOutline, DeleteOutlineOutlined, InfoOutlined } from '@mui/icons-material';
import { IconButton, Stack, ToggleButton, ToggleButtonGroup, alpha } from '@mui/material';
import { Tooltip } from '../';
import { NodeInputType } from '@components/Flow/Nodes';
import { useTheme } from '../../styles';
import { ReactNode } from 'react';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  min-height: 21px;
  max-height: 21px;
`;

const LabelContainer = styled.div<{ hoverEffect?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;

  color: ${({ theme }) => theme.text.standard};

  & .MuiSvgIcon-root {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: ${({ hoverEffect, theme }) => alpha(theme.text.standard, hoverEffect ? 1 : 0.5)};
  }

  p {
    font-size: 13px !important;
    font-weight: 500;
    color: ${({ theme }) => alpha(theme.text.standard, 0.8)};
    margin-bottom: 0;
  }

  &:hover {
    cursor: pointer;

    p {
      color: ${({ theme }) => theme.text.standard};
    }

    svg {
      color: ${({ theme }) => theme.text.standard};
    }
  }
`;

const Switch = styled.div`
  & .MuiToggleButton-root {
    padding: 0 8px;
    font-size: 11px;
    text-transform: none;
    border-radius: 4px;
    border-color: ${({ theme }) => theme.line.light};
  }

  display: none;
`;

const EndLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  p {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre;
    color: ${({ theme }) => theme.text.subtext};
    font-size: 13px;
  }

  svg {
    font-size: 13px !important;
  }

  &:hover {
    cursor: pointer;

    p {
      color: ${({ theme }) => theme.primary};
    }

    svg {
      color: ${({ theme }) => theme.primary} !important;
    }
  }
`;

interface Props {
  title: string;
  onClick?: (e: any) => void;
  onDelete?: () => void;
  tooltip?: string;
  alignment?: string | null;
  setAlignment?: React.Dispatch<string | null>;
  optionAlignment?: boolean;
  monospaceTooltip?: boolean;
  customOption?: {
    label: string;
    value: string;
  };
  endLabel?: {
    label: string;
    icon?: ReactNode;
    tooltip?: string;
  };
}

export const Label: React.FC<Props> = ({
  title,
  onClick,
  onDelete,
  tooltip,
  alignment,
  setAlignment,
  optionAlignment,
  monospaceTooltip,
  customOption,
  endLabel,
}) => {
  const theme = useTheme();

  const handleAlignment = (event: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
    if (typeof setAlignment !== 'function') return;

    if (!newAlignment) return;
    setAlignment(newAlignment);
  };

  const getInputSwitchLabel = () => {
    if (customOption) return customOption.label;
    if (optionAlignment) return 'Options';
    return 'Fixed';
  };

  const getInputSwitchValue = () => {
    if (customOption) return customOption.value;
    if (optionAlignment) return NodeInputType.Option;
    return NodeInputType.Text;
  };

  const item = (
    <LabelContainer>
      {tooltip && <InfoOutlined />}
      <p>{title}</p>
      {typeof onClick === 'function' && (
        <IconButton size='small' onClick={onClick} color='primary'>
          <AddCircleOutline sx={{ fontSize: '14px', color: `${theme.text.standard} !important` }} />
        </IconButton>
      )}
      {typeof onDelete === 'function' && (
        <IconButton size='small' onClick={onDelete} color='secondary'>
          <DeleteOutlineOutlined sx={{ fontSize: '14px' }} />
        </IconButton>
      )}
    </LabelContainer>
  );

  const toggleSwitch = (
    <Switch className='switch'>
      <ToggleButtonGroup
        color='primary'
        size='small'
        value={alignment}
        exclusive
        onChange={handleAlignment}
        aria-label='input type'
      >
        <ToggleButton value={getInputSwitchValue()} aria-label='fixed'>
          {getInputSwitchLabel()}
        </ToggleButton>
        <ToggleButton value='expression' aria-label='expression'>
          Expression
        </ToggleButton>
      </ToggleButtonGroup>
    </Switch>
  );

  const endLabelComponent = (
    <EndLabel>
      {endLabel?.icon ? endLabel.icon : null}
      <p>{endLabel?.label}</p>
    </EndLabel>
  );

  const endLabelTooltip = endLabel?.tooltip ? (
    <Tooltip sx={{ maxWidth: '300px' }} placement='top' arrow title={endLabel?.tooltip}>
      {endLabelComponent}
    </Tooltip>
  ) : (
    endLabelComponent
  );

  if (tooltip) {
    return (
      <Container>
        <Tooltip title={tooltip} arrow placement='left' sx={{ maxWidth: 420 }} monospaceTooltip={monospaceTooltip}>
          {item}
        </Tooltip>
        <Stack direction='row' spacing={1} alignItems='center'>
          {typeof setAlignment === 'function' && alignment && toggleSwitch}
          {endLabel ? endLabelTooltip : null}
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      {item}
      <Stack direction='row' spacing={1} alignItems='center'>
        {typeof setAlignment === 'function' && alignment && toggleSwitch}
        {endLabel ? endLabelTooltip : null}
      </Stack>
    </Container>
  );
};
